import { useCallback, useEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { Box } from 'theme-ui'
import { Nav } from 'components'
import { useAuth, useConfigUtils } from 'providers'
import { useConfirmation } from 'hooks/useConfirmation'
import { useCreate } from 'hooks/userHooks'
import { CreateStudentForm } from 'components/UserForm/CreateStudentForm'
import { StudentFormContentLoader } from 'components/UserForm/StudentFormContentLoader'
import { StudentCreateFormValues } from 'types'
import getLocalized from 'i18n'
import * as access from 'types/access'

const localized = getLocalized()

export function Create() {
  useEffect(() => {
    document.title = localized.documentTitles.adminDashboard
  }, [])

  const { profile, logout, hasAccess } = useAuth()
  const history = useHistory()
  const utils = useConfigUtils()
  const [state, create] = useCreate()
  const isCustomerSupportUser = profile.groups.includes('SupportGroup')

  async function handleLogoutClick() {
    await logout(profile.email)
    history.push({
      pathname: '/',
      state: undefined,
    })
  }
  const navigateToDashboard = useCallback(() => {
    history.push({ pathname: '/', state: null })
  }, [history])
  const hasNavigated = useRef<boolean>(false)
  useEffect(() => {
    if (state.matches('success') && !hasNavigated.current) {
      hasNavigated.current = true
      navigateToDashboard()
    }
  }, [state, navigateToDashboard])

  // MFI-2610 verify-on-create: the email already has an eligible
  // (non-verified / non-meditator) app account. Office-agreed rules (07-17):
  // a plain non-meditator record upgrades with NO confirmation (the create is
  // routine office work); a PENDING record is a meditator claim — National
  // proceeds without a modal, Customer Support confirms first.
  const lastFormDataRef = useRef<Record<string, unknown> | null>(null)
  const handledUpgradeErrorRef = useRef<unknown>(null)
  const confirmVerifyTakeover = useConfirmation({
    header: localized.confirmationModal.upgradeExistingVerify.header,
    body: localized.confirmationModal.upgradeExistingVerify.body,
    confirmText: localized.confirmationModal.upgradeExistingVerify.confirmText,
  })

  const failedError = state.matches('failed')
    ? (state.context.data as
        | (Error & {
            responseData?: {
              code?: string
              data: { id: string; existing?: { nonVerified?: boolean } }
            }
          })
        | undefined)
    : undefined
  const isUpgradeable =
    failedError?.responseData?.code === 'EmailExistsUpgradeable'
  const isNationalOffice = profile?.groups?.includes('TeacherAdminGroup')

  useEffect(() => {
    if (
      !isUpgradeable ||
      !failedError ||
      handledUpgradeErrorRef.current === failedError
    ) {
      return
    }
    handledUpgradeErrorRef.current = failedError
    const resubmit = () => {
      if (lastFormDataRef.current) {
        create({
          ...lastFormDataRef.current,
          upgradeExisting: true,
        } as StudentCreateFormValues & { upgradeExisting: boolean })
      }
    }
    // Pending unless the summary explicitly says non-meditator — when the
    // summary is missing, err toward asking.
    const isPending =
      failedError.responseData?.data?.existing?.nonVerified !== false
    if (!isPending || isNationalOffice) {
      resubmit()
      return
    }
    confirmVerifyTakeover().then((confirmed) => {
      if (confirmed) resubmit()
    })
  }, [
    isUpgradeable,
    failedError,
    isNationalOffice,
    confirmVerifyTakeover,
    create,
  ])

  function handleFormSubmit(values: StudentCreateFormValues) {
    const formData = {
      teacher: profile.id,
      ...values,
    }
    lastFormDataRef.current = formData
    create(formData)
  }

  const isTeacherTraining = !hasAccess(
    access.Groups.DASHBOARD,
    access.Dashboard.CREATE_USERS
  )

  const canCreateStudents =
    hasAccess(access.Groups.DASHBOARD, access.Dashboard.CREATE_TEST_USERS) ||
    hasAccess(access.Groups.DASHBOARD, access.Dashboard.CREATE_USERS)

  const canCreateTeachers = profile?.groups?.includes('SupportGroup')

  const canViewHelpCenter = hasAccess(
    access.Groups.DASHBOARD,
    access.Dashboard.VIEW_HELP_CENTER
  )
  const canViewDropdownDate = hasAccess(
    access.Groups.DASHBOARD,
    access.Dashboard.NO_DATE_OF_INSTRUCTION_RESTRICTION
  )

  return (
    <Box
      data-cy="create-page"
      sx={{
        bg: 'whitesmoke',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
      }}
    >
      <Nav
        profile={profile}
        onLogoutClick={handleLogoutClick}
        access={{
          createStudent: canCreateStudents,
          createTeacher: canCreateTeachers,
          viewHelpCenter: canViewHelpCenter,
        }}
      />
      <Box
        data-cy="student-create"
        sx={{
          width: '100vw',
          flexDirection: 'column',
          flex: '1 0 auto',
          alignItems: 'center',
          bg: 'white',
          pt: 68,
          position: 'relative',
        }}
      >
        {utils ? (
          <CreateStudentForm
            pending={state.matches('syncing')}
            disabled={state.matches('syncing')}
            configUtils={utils}
            responseError={
              state.matches('failed') && !isUpgradeable
                ? state.context.error
                : undefined
            }
            isSupport={isCustomerSupportUser}
            isTeacherTraining={isTeacherTraining}
            onFormSubmit={handleFormSubmit}
            onFormCancel={navigateToDashboard}
            access={{
              viewDropdownDate: canViewDropdownDate,
            }}
          />
        ) : (
          <Box variant="userProfile.form">
            <StudentFormContentLoader />
          </Box>
        )}
      </Box>
    </Box>
  )
}
